import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Calculator,
  Search,
  FileText,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Zap,
  FileCheck,
  FileSpreadsheet,
  Scale,
  Calendar,
  Download,
  AlertTriangle,
  FolderOpen,
  Clock,
  Bell,
  PenTool,
  Bookmark,
  GraduationCap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

type SolutionKey =
  | "cartera_ph"
  | "seguimiento_casos"
  | "redaccion_documentos"
  | "calculadoras"
  | "investigacion"
  | "campus"
  | "consultorio";

interface SolutionData {
  id: SolutionKey;
  tabLabel: string;
  icon: React.ElementType;
  badge: string;
  badgeColor: string;
  title: string;
  subtitle: string;
  description: string;
  highlights: string[];
  metricNumber: string;
  metricLabel: string;
  ctaText: string;
  ctaLink: string;
}

const solutionsData: Record<SolutionKey, SolutionData> = {
  cartera_ph: {
    id: "cartera_ph",
    tabLabel: "Cartera en Propiedad Horizontal",
    icon: Building2,
    badge: "Módulo Especializado Ley 675",
    badgeColor: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    title: "Cobranza Automatizada de Expensas Comunes con Botón PSE",
    subtitle: "Recupera cartera prejurídica en copropiedades sin fricción y con plena validez judicial.",
    description:
      "Gestiona la cobranza masiva de cuotas ordinarias y extraordinarias enviando recordatorios amables y firmes por WhatsApp y correo con botón directo de pago PSE. Emite el Título Ejecutivo del Art. 48 de la Ley 675 en 1 solo clic y opera con estricto apego a los horarios de la Ley 2300 de 2023.",
    highlights: [
      "Notificaciones automáticas por WhatsApp con link de recaudo inmediato (PSE/Nequi).",
      "Certificado de deuda con fuerza ejecutiva inmediata (Art. 48 Ley 675).",
      "Auditoría horaria y límite de contactos para blindaje ante la SIC (Ley 2300).",
      "Trazabilidad de pagos y acuerdos de pago en línea para copropietarios.",
    ],
    metricNumber: "40%+",
    metricLabel: "Mayor velocidad de recuperación en fase prejurídica",
    ctaText: "Conoce el Módulo Cartera PH",
    ctaLink: "/cartera-ph",
  },
  seguimiento_casos: {
    id: "seguimiento_casos",
    tabLabel: "Control y Seguimiento de Expedientes",
    icon: FolderOpen,
    badge: "Sincronizado con la Rama Judicial",
    badgeColor: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30",
    title: "Monitoreo Inteligente de Procesos Judiciales y Términos",
    subtitle: "Rastreo continuo de radicados de 23 dígitos, autos, traslados y alertas tempranas.",
    description:
      "Automatiza la vigilancia de tus procesos en despachos judiciales de todo el país. El sistema extrae en tiempo real las actuaciones, fija fechas en el calendario procesal en días hábiles (descontando vacancias y festivos) y te notifica con antelación para que nunca se venza un término procesal ni caduque una acción.",
    highlights: [
      "Búsqueda y vinculación automática por radicado de 23 dígitos o nombre de partes.",
      "Detección instantánea de autos, estados electrónicos, traslados y fijaciones en lista.",
      "Cómputo automático de términos procesales con calendario judicial de Colombia.",
      "Historial cronológico de providencias y almacenamiento seguro de anexos y cuadernos.",
    ],
    metricNumber: "0",
    metricLabel: "Términos procesales vencidos o inadvertidos en tu despacho",
    ctaText: "Conoce el Módulo de Control de Expedientes",
    ctaLink: "/control-expedientes",
  },
  redaccion_documentos: {
    id: "redaccion_documentos",
    tabLabel: "Generación de Documentos Jurídicos",
    icon: PenTool,
    badge: "Redactor Especializado con IA",
    badgeColor: "bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-500/30",
    title: "Redacción Automatizada de Tutelas, Demandas, Minutas y Recursos",
    subtitle: "Crea piezas procesales y contratos completos estructurados con hechos y normas colombianas.",
    description:
      "A partir de una breve descripción de los hechos o datos de las partes, la IA genera documentos jurídicos completos y listos para radicar: Acciones de Tutela con medidas cautelares, Derechos de Petición con plazos del CPACA, Demandas ejecutivas y ordinarias, Contestaciones con excepciones, Recursos de reposición/apelación y contratos comerciales o civiles.",
    highlights: [
      "Acciones de Tutela y Derechos de Petición fundamentados en jurisprudencia constitucional.",
      "Demandas Civiles, Laborales y Comerciales con hechos, pretensiones y acápite probatorio.",
      "Recursos de Reposición y Apelación con sustento en causales legales y doctrina.",
      "Contratos y minutas (arrendamiento, prestación de servicios, acuerdos societarios y poderes).",
    ],
    metricNumber: "10x",
    metricLabel: "Mayor rapidez en elaboración de memoriales y minutas",
    ctaText: "Probar Redactor Jurídico",
    ctaLink: "/features/document-drafting",
  },
  calculadoras: {
    id: "calculadoras",
    tabLabel: "Calculadoras Judiciales",
    icon: Calculator,
    badge: "Actualizado IPC DANE & Fórmulas Oficiales CSJ",
    badgeColor: "bg-teal-500/15 text-teal-700 dark:text-teal-400 border-teal-500/30",
    title: "Suite de Liquidaciones y Cuantificaciones Judiciales",
    subtitle: "Cálculos matemáticos-jurídicos en menos de 3 segundos con fórmulas de las Altas Cortes.",
    description:
      "Elimina errores aritméticos y horas en hojas de cálculo. Calcula perjuicios por traslado pensional (Corte Suprema de Justicia), liquidaciones laborales (Art. 64 CST), perjuicios civiles con indexación DANE, sentencias contenciosas y dosimetría penal.",
    highlights: [
      "Perjuicios por Traslado Pensional: Fórmula oficial de pérdida de oportunidad de la Sala Laboral CSJ.",
      "Liquidación Laboral: Cesantías, prima, vacaciones e indemnizaciones por despido.",
      "Perjuicios Civiles: Daño emergente y lucro cesante consolidado y futuro.",
      "Administrativo: Doble fase de condena estatal (DTF + Mora) sin anatocismo.",
    ],
    metricNumber: "< 3 seg",
    metricLabel: "Tiempo promedio por cuantificación con memoria probatoria",
    ctaText: "Explorar Suite de Calculadoras",
    ctaLink: "/calculadoras",
  },
  investigacion: {
    id: "investigacion",
    tabLabel: "Investigación & Jurisprudencia",
    icon: Search,
    badge: "Base de +1.8M Archivos Oficiales",
    badgeColor: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    title: "Búsqueda Semántica con Citas Directas a Providencias",
    subtitle: "Razonamiento jurídico profundo en la relatoría de Altas Cortes y normas colombianas.",
    description:
      "Nuestros agentes no inventan respuestas: extraen y sintetizan la doctrina vinculante de la Corte Constitucional (SU, C, T), Corte Suprema de Justicia y Consejo de Estado. Cada concepto incluye el número de radicado, magistrado ponente y enlace al texto oficial.",
    highlights: [
      "Cero alucinaciones: todas las afirmaciones cuentan con cita de soporte comprobable.",
      "Líneas jurisprudenciales automáticas y detección de cambios de criterio de las salas.",
      "Compilación completa de Códigos Nacionales, Decretos Únicos y Leyes del Congreso.",
      "Integración directa con tu redactor para insertar citas textuales con formato APA/Judicial.",
    ],
    metricNumber: "1.8M+",
    metricLabel: "Sentencias, autos y leyes indexados y actualizados",
    ctaText: "Ver Asistente de Investigación",
    ctaLink: "/features/legal-research",
  },
  campus: {
    id: "campus",
    tabLabel: "MIA Campus & Taller Socrático",
    icon: GraduationCap,
    badge: "Formación Jurídica Clínica",
    badgeColor: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-400 border-indigo-500/30",
    title: "Entrenamiento Jurídico con IA y Auditor de Citas Reales",
    subtitle: "Taller socrático interactivo que cuestiona argumentos y valida jurisprudencia con la relatoría oficial.",
    description:
      "Diseñado para facultades de derecho, semilleros y litigantes en perfeccionamiento. El Taller Socrático somete memoriales y argumentos a contrapreguntas dialécticas, mientras que el Auditor de Citas verifica en tiempo real que cada sentencia citada exista realmente y pertenezca a la línea jurisprudencial de la Corte Constitucional, CSJ o Consejo de Estado.",
    highlights: [
      "Auditor de Citas en vivo: verificación de sentencias reales vs. alucinaciones de IA con semáforo de confianza.",
      "Taller Socrático interactivo: objeciones procesales y dialéctica jurídica guiada por el modelo.",
      "Banco de casos clínicos clasificados por área del derecho y nivel de complejidad.",
      "Rúbricas automáticas de evaluación analítica y retroalimentación argumentativa inmediata.",
    ],
    metricNumber: "100%",
    metricLabel: "Trazabilidad de citas y jurisprudencia verificada en relatorías oficiales",
    ctaText: "Explorar MIA Campus",
    ctaLink: "/dashboard/campus-consultorio",
  },
  consultorio: {
    id: "consultorio",
    tabLabel: "Consultorio Jurídico (Ley 2113)",
    icon: Scale,
    badge: "Cumplimiento Ley 2113 de 2021",
    badgeColor: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    title: "Gestión Clínica, Control de Cuantía SMLMV y Doble Firma",
    subtitle: "Flujo integral para estudiantes, practicantes y docentes asesores con validez probatoria Ley 527.",
    description:
      "Digitaliza y blinda la operación de los consultorios jurídicos universitarios. Valida automáticamente la competencia por cuantía en salarios mínimos (SMLMV), gestiona la revisión escalonada de memoriales y aplica doble firma electrónica con hash SHA-256 e integridad probatoria conforme a la Ley 527 de 1999.",
    highlights: [
      "Filtro preventivo de cuantía (hasta 50 SMLMV) y competencia por materias según Ley 2113 de 2021.",
      "Flujo de revisión docente: aprobación, observaciones y visto bueno del asesor de área.",
      "Doble firma digital (estudiante y docente asesor) con sellado criptográfico y trazabilidad.",
      "Expediente digital único del usuario con actas de compromiso y estado procesal en tiempo real.",
    ],
    metricNumber: "50 SMLMV",
    metricLabel: "Control automatizado de competencia y cuantía legal por caso",
    ctaText: "Ver Consultorio Jurídico",
    ctaLink: "/dashboard/campus-consultorio",
  },
};

export const SolutionsShowcase: React.FC = () => {
  const [activeTab, setActiveTab] = useState<SolutionKey>("cartera_ph");
  const activeSolution = solutionsData[activeTab];

  return (
    <section id="features" className="py-16 lg:py-20 bg-slate-50 dark:bg-slate-950 relative overflow-hidden">
      {/* Anchor for calculadoras */}
      <div id="calculadoras-juridicas" className="absolute -top-20" />

      {/* Background ambient glows */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[900px] h-[450px] bg-gradient-to-tr from-blue-500/10 via-indigo-500/5 to-transparent rounded-full blur-[110px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-10 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-100/80 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-900 text-blue-700 dark:text-blue-300 text-xs font-bold uppercase tracking-wider mb-4 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
            <span>Plataforma Integral de Soluciones Legales</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-tight mb-4">
            Todas las herramientas de tu despacho en{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 dark:from-blue-400 dark:to-indigo-300">
              un solo espacio interactivo
            </span>
          </h2>

          <p className="text-slate-600 dark:text-slate-300 text-base sm:text-lg leading-relaxed">
            Sin tener que contratar múltiples herramientas aisladas ni navegar páginas interminables.
            Selecciona una solución para verla en funcionamiento:
          </p>
        </div>

        {/* Tab Navigation Segmented Bar (Clean, wrapped or scrollable with no ugly scrollbars) */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-2.5 mb-8 sm:mb-10 px-1">
          {(Object.keys(solutionsData) as SolutionKey[]).map((key) => {
            const item = solutionsData[key];
            const Icon = item.icon;
            const isActive = activeTab === key;

            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-4 sm:px-4.5 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all duration-300 shadow-sm border ${isActive
                    ? "bg-slate-900 dark:bg-white text-white dark:text-slate-950 border-slate-900 dark:border-white shadow-md shadow-slate-900/10 scale-[1.02]"
                    : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200/90 dark:border-slate-800 hover:border-blue-300 dark:hover:border-slate-700 hover:text-slate-900 dark:hover:text-white"
                  }`}
              >
                <div
                  className={`p-1 rounded-lg transition-colors ${isActive
                      ? "bg-white/20 dark:bg-slate-950/20 text-white dark:text-slate-950"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                    }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <span>{item.tabLabel}</span>
              </button>
            );
          })}
        </div>

        {/* Main Showcase Bento Panel */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-3xl p-6 sm:p-8 lg:p-10 shadow-xl shadow-slate-900/5 relative overflow-hidden transition-all duration-500">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">

            {/* Left Column: Information & Key Value Prop (7 cols) */}
            <div className="lg:col-span-7 flex flex-col justify-between space-y-5">
              <div>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${activeSolution.badgeColor}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {activeSolution.badge}
                  </span>
                </div>

                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 dark:text-white leading-tight mb-2.5">
                  {activeSolution.title}
                </h3>

                <p className="text-base sm:text-lg font-medium text-slate-700 dark:text-slate-300 mb-3">
                  {activeSolution.subtitle}
                </p>

                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-5">
                  {activeSolution.description}
                </p>
              </div>

              {/* Highlights Checkmark List */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                {activeSolution.highlights.map((highlight, idx) => (
                  <div key={idx} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 flex items-center justify-center shrink-0 mt-0.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300 leading-snug">
                      {highlight}
                    </span>
                  </div>
                ))}
              </div>

              {/* Stat & Action CTA Row */}
              <div className="pt-5 border-t border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
                <div className="flex items-baseline gap-3">
                  <span className="text-3xl sm:text-4xl font-black text-blue-600 dark:text-blue-400 tracking-tight">
                    {activeSolution.metricNumber}
                  </span>
                  <span className="text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400 max-w-[220px] leading-tight">
                    {activeSolution.metricLabel}
                  </span>
                </div>

                <Link to={activeSolution.ctaLink} className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl px-6 h-12 shadow-lg shadow-blue-600/20 hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base"
                  >
                    <span>{activeSolution.ctaText}</span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Column: Interactive UI Preview Simulation (5 cols) */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-950 text-white p-5 sm:p-6 shadow-2xl border border-slate-800 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500/10 rounded-full blur-2xl pointer-events-none" />

                {/* Simulated UI Window Bar */}
                <div className="flex items-center justify-between border-b border-slate-800 pb-3.5 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-rose-500/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
                    <span className="text-xs font-mono text-slate-400 ml-2">
                      {activeTab === "cartera_ph" && "cartera-ph.ley675"}
                      {activeTab === "seguimiento_casos" && "rama-judicial.expedientes"}
                      {activeTab === "redaccion_documentos" && "redactor.documentos.ia"}
                      {activeTab === "calculadoras" && "calculadoras-cst.dane"}
                      {activeTab === "investigacion" && "rag-jurisprudencia.1.8M"}
                      {activeTab === "campus" && "mia-campus.taller-socratico"}
                      {activeTab === "consultorio" && "consultorio.ley-2113"}
                    </span>
                  </div>
                  <Badge variant="outline" className="text-[10px] text-emerald-400 border-emerald-500/30 font-mono">
                    ONLINE
                  </Badge>
                </div>

                {/* 1. Cartera PH Preview */}
                {activeTab === "cartera_ph" && (
                  <div className="space-y-3.5">
                    <div className="bg-slate-800/70 border border-slate-700/60 rounded-xl p-4">
                      <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
                        <span className="flex items-center gap-1.5 text-emerald-400 font-bold">
                          <Building2 className="w-3.5 h-3.5" /> Edificio Altos del Parque
                        </span>
                        <span>Apto 804</span>
                      </div>
                      <div className="text-lg font-bold text-white mb-1">
                        Saldo en Mora: $ 1.840.000 COP
                      </div>
                      <p className="text-xs text-slate-300 leading-relaxed mb-3">
                        3 cuotas ordinarias + intereses de mora según tasa Superfinanciera.
                      </p>
                      <div className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 w-full cursor-pointer transition-colors shadow">
                        <Zap className="w-3.5 h-3.5 text-amber-300" />
                        <span>Pagar con PSE / WhatsApp</span>
                      </div>
                    </div>

                    <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-3 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2 text-slate-300">
                        <FileCheck className="w-4 h-4 text-blue-400" />
                        <span>Certificado Art. 48 Ley 675</span>
                      </div>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30">
                        Título Ejecutivo Listo
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-[11px] text-slate-400">
                      <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span>Notificaciones dentro de los horarios de la Ley 2300 de 2023.</span>
                    </div>
                  </div>
                )}

                {/* 2. Seguimiento y Control de Casos Preview */}
                {activeTab === "seguimiento_casos" && (
                  <div className="space-y-3">
                    <div className="bg-slate-800/80 border border-blue-500/30 rounded-xl p-3.5 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono font-bold text-blue-400">
                          11001-31-03-014-2024-00412-00
                        </span>
                        <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px]">
                          Activo en Lista
                        </Badge>
                      </div>

                      <div className="text-xs font-semibold text-white">
                        Juzgado 14 Civil del Circuito de Bogotá
                      </div>
                      <div className="text-[11px] text-slate-400">
                        Proceso Ordinario • Inversiones Andinas S.A. vs. Constructora del Río
                      </div>
                    </div>

                    <div className="bg-amber-950/40 border border-amber-800/50 rounded-xl p-3 text-xs space-y-1.5">
                      <div className="flex items-center justify-between text-amber-300 font-bold text-[11px]">
                        <span className="flex items-center gap-1.5">
                          <Bell className="w-3.5 h-3.5" /> Última Actuación Notificada:
                        </span>
                        <span className="text-amber-400">Hace 2 horas</span>
                      </div>
                      <p className="text-slate-200 text-xs font-medium">
                        "Auto corre traslado de excepciones previas. Término legal: 3 días hábiles."
                      </p>
                      <div className="flex items-center justify-between pt-1 border-t border-amber-800/40 text-[11px]">
                        <span className="text-slate-400 flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-amber-400" /> Vence: Jueves 5:00 PM
                        </span>
                        <span className="text-emerald-400 font-bold">Alerta Agendada</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-slate-400 px-1 pt-1">
                      <span>Sincronización Tyba / Samai / Siglo XXI</span>
                      <span className="text-blue-400 font-bold">Alertas por WhatsApp</span>
                    </div>
                  </div>
                )}

                {/* 3. Redacción de Todo Tipo de Documentos Preview */}
                {activeTab === "redaccion_documentos" && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-1 bg-slate-800/70 p-1 rounded-xl text-center text-xs font-semibold">
                      <div className="py-1 px-1.5 bg-purple-600 text-white rounded-lg shadow text-[11px]">
                        Acción Tutela
                      </div>
                      <div className="py-1 px-1.5 text-slate-300 text-[11px]">
                        Demanda Ordinaria
                      </div>
                      <div className="py-1 px-1.5 text-slate-300 text-[11px]">
                        Derecho Petición
                      </div>
                    </div>

                    <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-3.5 space-y-2 text-xs">
                      <div className="flex items-center justify-between border-b border-slate-700/60 pb-1.5">
                        <span className="font-bold text-white flex items-center gap-1.5">
                          <FileText className="w-3.5 h-3.5 text-purple-400" />
                          TUTELA_SALUD_MEDIDA_CAUTELAR.docx
                        </span>
                        <span className="text-[10px] text-emerald-400 font-mono">Art. 86 C.P.</span>
                      </div>

                      <div className="space-y-1.5 text-[11px] text-slate-300">
                        <div className="flex items-start gap-1.5">
                          <span className="text-purple-400 font-bold shrink-0">I. Hechos:</span>
                          <span className="truncate">Paciente con diagnóstico prioritario requiere medicamento...</span>
                        </div>
                        <div className="flex items-start gap-1.5">
                          <span className="text-purple-400 font-bold shrink-0">II. Vulneración:</span>
                          <span>Derecho fundamental a la salud y vida digna (Sentencia T-760/08).</span>
                        </div>
                        <div className="flex items-start gap-1.5">
                          <span className="text-purple-400 font-bold shrink-0">III. Pretensión:</span>
                          <span>Medida cautelar innominada para entrega en 48 horas.</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-slate-800/40 p-2.5 rounded-xl border border-slate-700/40 flex items-center justify-between text-xs">
                      <span className="text-[11px] text-slate-300">Formato listo para firma y radicación</span>
                      <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-1">
                        <Download className="w-3 h-3" /> Descargar Word / PDF
                      </span>
                    </div>
                  </div>
                )}

                {/* 4. Calculadoras Judiciales Preview */}
                {activeTab === "calculadoras" && (
                  <div className="space-y-3.5">
                    <div className="grid grid-cols-4 gap-1 bg-slate-800/70 p-1 rounded-xl text-center text-xs font-semibold">
                      <div className="py-1 px-1 bg-amber-600 text-white rounded-lg shadow text-[10px] font-bold">
                        CSJ Traslado
                      </div>
                      <div className="py-1 px-1 text-slate-300 text-[10px]">
                        Art. 64 CST
                      </div>
                      <div className="py-1 px-1 text-slate-300 text-[10px]">
                        IPC DANE
                      </div>
                      <div className="py-1 px-1 text-slate-300 text-[10px]">
                        Penal Cuartos
                      </div>
                    </div>

                    <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-3.5 space-y-2">
                      <div className="flex justify-between text-xs border-b border-slate-700/60 pb-1.5">
                        <span className="text-slate-400">Pérdida de Oportunidad (PP):</span>
                        <span className="font-bold text-amber-300">0.3545 (35.45%)</span>
                      </div>
                      <div className="flex justify-between text-xs border-b border-slate-700/60 pb-1.5">
                        <span className="text-slate-400">Brecha Mesada (RPM − RAIS):</span>
                        <span className="font-bold text-white">$ 900.000 COP</span>
                      </div>
                      <div className="flex justify-between items-baseline pt-1">
                        <span className="text-xs text-amber-300 font-bold">Indemnización CSJ:</span>
                        <span className="text-lg font-black text-emerald-400">$ 88.341.345 COP</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-xs text-slate-300 bg-slate-800/40 p-2.5 rounded-xl border border-slate-700/40">
                      <div className="flex items-center gap-1.5">
                        <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-[11px]">Fórmula Sala Laboral Corte Suprema</span>
                      </div>
                      <Link to="/calculadora-perjuicios" className="text-[10px] font-bold text-amber-400 hover:underline uppercase tracking-wider flex items-center gap-1">
                        Calcular Ahora →
                      </Link>
                    </div>
                  </div>
                )}

                {/* 5. Investigación & Jurisprudencia Preview */}
                {activeTab === "investigacion" && (
                  <div className="space-y-3.5">
                    <div className="bg-slate-800/90 border border-blue-500/40 rounded-xl p-2.5 flex items-center gap-2 text-xs">
                      <Search className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                      <span className="text-slate-200 font-mono text-[11px] truncate">
                        "Precedente unificación fuero de salud sin calificación..."
                      </span>
                    </div>

                    <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-3 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Badge className="bg-amber-400/20 text-amber-300 border-amber-400/30 text-[10px] font-bold">
                          Sentencia SU-087/2022
                        </Badge>
                        <span className="text-[10px] text-slate-400">Corte Constitucional</span>
                      </div>
                      <p className="text-xs text-slate-300 leading-relaxed font-serif italic">
                        "...el derecho a la estabilidad laboral reforzada protege al trabajador que se encuentra en condición de debilidad manifiesta, con independencia de si cuenta o no con carné de discapacidad..."
                      </p>
                      <div className="flex items-center gap-1.5 pt-1 text-[11px] text-emerald-400 font-bold">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>Ratio Decidendi extraída y verificada</span>
                      </div>
                    </div>

                    <div className="text-[11px] text-slate-400 flex items-center justify-between px-1">
                      <span>Indexación: 650.000+ fallos C, T y SU</span>
                      <span className="text-blue-400 font-bold">100% Citas Reales</span>
                    </div>
                  </div>
                )}

                {/* 6. MIA Campus Preview */}
                {activeTab === "campus" && (
                  <div className="space-y-3">
                    <div className="bg-slate-800/80 border border-indigo-500/30 rounded-xl p-3.5 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono font-bold text-indigo-400">
                          Caso Clínico: Acción Popular #AP-2024-88
                        </span>
                        <Badge className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 text-[10px]">
                          Taller Socrático Activo
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-300 italic">
                        "¿Cuál es el nexo causal entre la omisión municipal alegada y la afectación al goce del espacio público?"
                      </div>
                    </div>

                    <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-3 space-y-2 text-xs">
                      <div className="flex items-center justify-between border-b border-slate-700/60 pb-1.5">
                        <span className="font-bold text-white flex items-center gap-1.5">
                          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                          Auditor de Citas Oficiales
                        </span>
                        <span className="text-[10px] text-emerald-400 font-mono font-bold">100% Verificado</span>
                      </div>
                      <div className="space-y-1.5 text-[11px] text-slate-300">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-300">Corte Constitucional SU-087/22</span>
                          <span className="text-emerald-400 font-semibold">Existente en Relatoría ✓</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-300">Consejo de Estado 25000-23-41</span>
                          <span className="text-emerald-400 font-semibold">Línea Vigente ✓</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-slate-400 px-1 pt-1">
                      <span>Rúbrica Pedagógica Automatizada</span>
                      <span className="text-indigo-400 font-bold">Feedback Dialéctico</span>
                    </div>
                  </div>
                )}

                {/* 7. Consultorio Jurídico Preview */}
                {activeTab === "consultorio" && (
                  <div className="space-y-3">
                    <div className="bg-slate-800/80 border border-emerald-500/30 rounded-xl p-3.5 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono font-bold text-emerald-400">
                          Caso #CJ-2024-0342 • Civil Familia
                        </span>
                        <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px]">
                          Ley 2113 de 2021
                        </Badge>
                      </div>
                      <div className="flex justify-between text-xs text-slate-300">
                        <span>Cuantía Solicitada:</span>
                        <span className="font-bold text-white">$ 24.500.000 COP (~18.8 SMLMV)</span>
                      </div>
                      <div className="text-[11px] text-emerald-400 font-semibold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Competencia válida (&lt; 50 SMLMV)
                      </div>
                    </div>

                    <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-3 space-y-2 text-xs">
                      <div className="flex items-center justify-between border-b border-slate-700/60 pb-1.5">
                        <span className="font-bold text-white flex items-center gap-1.5">
                          <FileCheck className="w-3.5 h-3.5 text-blue-400" />
                          Doble Firma Digital (Ley 527/99)
                        </span>
                        <span className="text-[10px] text-blue-400 font-mono">SHA-256</span>
                      </div>
                      <div className="space-y-1 text-[11px]">
                        <div className="flex items-center justify-between text-slate-300">
                          <span>1. Estudiante Practicante:</span>
                          <span className="text-emerald-400 font-medium">Firmado ✓</span>
                        </div>
                        <div className="flex items-center justify-between text-slate-300">
                          <span>2. Docente Asesor de Área:</span>
                          <span className="text-amber-400 font-medium">Revisado & Aprobado ✓</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-slate-400 px-1 pt-1">
                      <span>Expediente Clínico Digital</span>
                      <span className="text-emerald-400 font-bold">Auditoría Docente</span>
                    </div>
                  </div>
                )}

              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};

export default SolutionsShowcase;
